import {err, ok, Result} from "neverthrow"
import {
	Account,
	getAccount,
	getProduct,
	isDepositOrBatchOrFBOAccount,
	isDepositOrBatchOrFBOAccountOrWallet,
	isWalletAccount,
} from "./account"
import {ErrorDocument, OkDocument} from "./common"
import {DepositProduct, findDepositProducts} from "./depositProduct"
import {isAchPayment, Payment, PaymentDirection, PaymentStatus} from "./payment"
import {findWalletTerms, WalletTerms} from "./walletTerms"

export function isValidForClearing(payment: Payment) {
	return (
		isAchPayment(payment) &&
		payment.attributes.status === PaymentStatus.Clearing &&
		payment.attributes.direction == PaymentDirection.Debit
	)
}

export function isDepositProductDynamicClearingEnabled(productName: string, depositProducts: DepositProduct[]) {
	return depositProducts.some(
		(product) => product.attributes.name === productName && !!product.attributes.enableDynamicClearingDaysAchDebit
	)
}

export function isWalletTermsDynamicClearingEnabled(termsName: string, walletTerms: WalletTerms[]) {
	return walletTerms.some(
		(terms) => terms.attributes.name === termsName && !!terms.attributes.enableDynamicClearingDaysAchDebit
	)
}

// Coalesces concurrent calls for the same key into one in-flight request, so rows sharing an
// account/org don't each fire their own fetch. The entry is removed as soon as it settles, so
// later renders always re-fetch instead of serving stale or cross-session data.
const inFlight = new Map<string, Promise<unknown>>()

function coalesced<V, E>(key: string, fetch: () => Promise<Result<V, E>>): Promise<Result<V, E>> {
	const existing = inFlight.get(key) as Promise<Result<V, E>> | undefined
	if (existing) {
		return existing
	}
	const promise = fetch()
	inFlight.set(key, promise)
	promise.finally(() => inFlight.delete(key))
	return promise
}

function getCachedAccount(accessToken: string, accountId: string): Promise<Result<Account, ErrorDocument>> {
	return coalesced(`account:${accessToken}:${accountId}`, () => getAccount<Account>(accessToken, accountId))
}

async function fetchAllPages<T>(
	getPage: (offset: number, limit: number) => Promise<Result<OkDocument<T[]>, ErrorDocument>>
): Promise<Result<T[], ErrorDocument>> {
	const limit = 100
	const all: T[] = []
	let offset = 0
	while (true) {
		const page = await getPage(offset, limit)
		if (page.isErr()) {
			return err(page.error)
		}
		all.push(...page.value.data)
		const total = Number(page.value.meta?.pagination.total ?? all.length)
		offset += limit
		if (offset >= total) {
			return ok(all)
		}
	}
}

function getCachedDepositProducts(
	accessToken: string,
	orgId: string
): Promise<Result<DepositProduct[], ErrorDocument>> {
	return coalesced(`depositProducts:${accessToken}:${orgId}`, () =>
		fetchAllPages((offset, limit) => findDepositProducts(accessToken, offset, limit, [orgId]))
	)
}

function getCachedWalletTerms(accessToken: string, orgId: string): Promise<Result<WalletTerms[], ErrorDocument>> {
	return coalesced(`walletTerms:${accessToken}:${orgId}`, () =>
		fetchAllPages((offset, limit) => findWalletTerms(accessToken, offset, limit, [orgId]))
	)
}

async function isDynamicClearingEnabledForAccount(
	accessToken: string,
	account: Account
): Promise<Result<boolean, ErrorDocument>> {
	if (!isDepositOrBatchOrFBOAccountOrWallet(account)) {
		return ok(false)
	}

	const orgId = account.relationships.org.data.id
	const productName = getProduct(account)
	if (!orgId || !productName) {
		return ok(false)
	}

	if (isDepositOrBatchOrFBOAccount(account)) {
		const result = await getCachedDepositProducts(accessToken, orgId)
		return result.map((products) => isDepositProductDynamicClearingEnabled(productName, products))
	}

	if (isWalletAccount(account)) {
		const result = await getCachedWalletTerms(accessToken, orgId)
		return result.map((terms) => isWalletTermsDynamicClearingEnabled(productName, terms))
	}

	return ok(false)
}

/**
 * Whether "Clear ACH" should render/be enabled for this payment: the payment must be eligible
 * (isValidForClearing) and the account's Deposit Product / Wallet Terms must have Dynamic
 * Clearing Days enabled, otherwise the API rejects the clear.
 */
export async function fetchCanClearAchPayment(
	accessToken: string,
	payment: Payment
): Promise<Result<boolean, ErrorDocument>> {
	if (!isValidForClearing(payment)) {
		return ok(false)
	}

	const accountResult = await getCachedAccount(accessToken, String(payment.relationships.account.data.id))
	if (accountResult.isErr()) {
		return err(accountResult.error)
	}

	return isDynamicClearingEnabledForAccount(accessToken, accountResult.value)
}
